import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Package, 
  Search, 
  Filter,
  Star,
  Battery,
  Wifi,
  Volume2,
  Headphones
} from "lucide-react";
import { toast } from "sonner";

export default function CatalogPage() {
  const { user, isSuperAdmin } = useAuth();
  const [hearingAids, setHearingAids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [formFactorFilter, setFormFactorFilter] = useState("all");

  useEffect(() => {
    fetchHearingAids();
  }, []);

  const fetchHearingAids = async () => {
    try {
      const response = await apiClient.get("/hearing-aids");
      setHearingAids(response.data);
    } catch (error) {
      console.error("Failed to fetch hearing aids:", error);
      toast.error("Failed to load catalog");
    } finally {
      setLoading(false);
    }
  };

  const brands = [...new Set(hearingAids.map(h => h.brand))];
  const formFactors = [...new Set(hearingAids.map(h => h.form_factor))];

  const filteredAids = hearingAids.filter(aid => {
    const matchesSearch = aid.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         aid.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBrand = brandFilter === "all" || aid.brand === brandFilter;
    const matchesFormFactor = formFactorFilter === "all" || aid.form_factor === formFactorFilter;
    return matchesSearch && matchesBrand && matchesFormFactor;
  });

  const getTechLevelLabel = (level) => {
    const labels = {
      1: "Essential",
      2: "Standard",
      3: "Advanced",
      4: "Premium",
      5: "Premium Plus",
      6: "Elite",
      7: "Ultimate"
    };
    return labels[level] || `Level ${level}`;
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="catalog-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Product Catalog</h1>
            <p className="text-slate-500">Browse hearing aids from top brands</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="catalog-search"
                />
              </div>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map(brand => (
                    <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={formFactorFilter} onValueChange={setFormFactorFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Form Factors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Form Factors</SelectItem>
                  {formFactors.map(ff => (
                    <SelectItem key={ff} value={ff}>{ff}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <p className="text-sm text-slate-500">
          Showing {filteredAids.length} of {hearingAids.length} products
        </p>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            [1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="skeleton h-72 rounded-xl"></div>
            ))
          ) : filteredAids.length > 0 ? (
            filteredAids.map(aid => (
              <Card key={aid.id} className="card-hover overflow-hidden" data-testid={`product-card-${aid.id}`}>
                {/* Product Image */}
                <div className="h-40 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                  {aid.image_url ? (
                    <img 
                      src={aid.image_url} 
                      alt={aid.name}
                      className="h-32 w-32 object-contain"
                    />
                  ) : (
                    <Headphones className="w-16 h-16 text-slate-300" />
                  )}
                </div>
                
                <CardContent className="p-4">
                  {/* Brand & Tech Level */}
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">{aid.brand}</Badge>
                    <Badge className="bg-violet-100 text-violet-700">
                      {getTechLevelLabel(aid.tech_level)}
                    </Badge>
                  </div>

                  {/* Name */}
                  <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">{aid.name}</h3>
                  <p className="text-sm text-slate-500 mb-3">{aid.form_factor}</p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {aid.rechargeable && (
                      <Badge variant="secondary" className="text-xs">
                        <Battery className="w-3 h-3 mr-1" />
                        Rechargeable
                      </Badge>
                    )}
                    {aid.streaming && (
                      <Badge variant="secondary" className="text-xs">
                        <Wifi className="w-3 h-3 mr-1" />
                        Bluetooth
                      </Badge>
                    )}
                    {aid.tinnitus_masking && (
                      <Badge variant="secondary" className="text-xs">
                        <Volume2 className="w-3 h-3 mr-1" />
                        Tinnitus
                      </Badge>
                    )}
                  </div>

                  {/* Price */}
                  <div className="flex items-end justify-between pt-3 border-t border-slate-100">
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        ₹{aid.price?.toLocaleString()}
                      </p>
                      {aid.price_pair && aid.price_pair !== aid.price && (
                        <p className="text-xs text-slate-500">
                          Pair: ₹{aid.price_pair?.toLocaleString()}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {aid.suitable_loss_range} loss
                    </Badge>
                  </div>

                  {/* Warranty */}
                  <p className="text-xs text-slate-400 mt-2">
                    {aid.warranty_years} year warranty
                  </p>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No products found matching your criteria</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
